import { useState, useCallback } from "react";
import { useGraphQLFetch } from "./use-graphql-fetch";
import { toast } from "sonner";
import { StockTake, StockTakeStatus, StockTakeType, RecurrenceType } from "@/data/mockStockTakes";

// Pagination info from GraphQL connection
export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
}

// Sort direction type
export type SortDirection = "ASC" | "DESC";

// Sort field enum values (matching backend StockTakeSort enum)
export type StockTakeSortField =
  | "StockTakeReference"
  | "Description"
  | "StockTakeType"
  | "Status"
  | "Warehouse"
  | "CreatedAt";

// Sort input for stock takes
export interface StockTakeSortInput {
  field: StockTakeSortField;
  order: SortDirection;
}

// Filter input for stock takes
export interface StockTakeFilterInput {
  stockTakeReference?: { contains?: string; eq?: string };
  description?: { contains?: string };
  stockTakeType?: { eq?: string };
  status?: { eq?: string };
  warehouse?: { contains?: string; eq?: string };
}

// GraphQL response types
interface StockTakeNode {
  stockTakeID: number;
  stockTakeReference: string;
  description: string;
  stockTakeType: string;
  warehouse: string;
  warehouseCode: string;
  status: string;
  createdBy: string;
  assignedTo: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  totalItems: number;
  countedItems: number;
  varianceCount: number;
  progressPercent: number;
  notes: string | null;
  lastSavedAt: string | null;
  lastSavedBy: string | null;
  modifiedAt: string;
  recurrence: string | null;
  nextScheduledDate: string | null;
  isSystemGenerated: boolean;
  abcClass: string | null;
}

// GraphQL connection types for stock takes
interface StockTakeConnection {
  items: StockTakeNode[];
  pageInfo: PageInfo;
  totalCount?: number;
}

interface GraphQLResponse {
  data?: {
    stockTakes?: {
      stockTakeList?: StockTakeConnection;
    };
  };
  errors?: Array<{ message: string }>;
}

// Map backend response to frontend StockTake type
const mapBackendToFrontend = (node: StockTakeNode): StockTake => {
  return {
    id: `ST-${node.stockTakeID}`,
    reference: node.stockTakeReference,
    description: node.description,
    type: (node.stockTakeType as StockTakeType) || "Full Stock Take",
    warehouse: node.warehouse,
    warehouseCode: node.warehouseCode,
    status: (node.status as StockTakeStatus) || "Started",
    createdBy: node.createdBy,
    createdAt: new Date(node.createdAt),
    assignedTo: node.assignedTo || "Unassigned",
    startedAt: node.startedAt ? new Date(node.startedAt) : undefined,
    completedAt: node.completedAt ? new Date(node.completedAt) : undefined,
    items: [],
    totalItems: node.totalItems,
    countedItems: node.countedItems,
    varianceCount: node.varianceCount,
    notes: node.notes || undefined,
    lastSavedAt: node.lastSavedAt ? new Date(node.lastSavedAt) : undefined,
    lastSavedBy: node.lastSavedBy || undefined,
    recurrence: (node.recurrence as RecurrenceType) || "once",
    nextScheduledDate: node.nextScheduledDate ? new Date(node.nextScheduledDate) : undefined,
    isSystemGenerated: node.isSystemGenerated,
    abcClass: (node.abcClass as "A" | "B" | "C") || undefined,
  };
};

// GraphQL query for stock takes with filters, sorting, and pagination
const STOCK_TAKES_QUERY = `
  query GetStockTakes(
    $first: Int, 
    $after: String, 
    $filter: StockTakeFilterInput,
    $sort: [StockTakeSortSortByInput!]
  ) {
    stockTakes {
      stockTakeList(
        first: $first, 
        after: $after, 
        filter: $filter,
        sort: $sort
      ) {
        items {
          stockTakeID
          stockTakeReference
          description
          stockTakeType
          warehouse
          warehouseCode
          status
          createdBy
          assignedTo
          createdAt
          startedAt
          completedAt
          totalItems
          countedItems
          varianceCount
          progressPercent
          notes
          lastSavedAt
          lastSavedBy
          modifiedAt
          recurrence
          nextScheduledDate
          isSystemGenerated
          abcClass
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

// GraphQL mutation to create a stock take
const CREATE_STOCK_TAKE_MUTATION = `
  mutation CreateStockTake($stockTake: StockTakeInput!) {
    stockTakes {
      stockTakeRecords {
        create(stockTake: $stockTake) {
          __typename
          ... on IntEntityId {
            id
          }
        }
      }
    }
  }
`;

// GraphQL mutation to update a stock take
const UPDATE_STOCK_TAKE_MUTATION = `
  mutation UpdateStockTake($id: Int!, $stockTake: StockTakeInput!) {
    stockTakes {
      stockTakeRecords {
        update(id: $id, stockTake: $stockTake) {
          __typename
          ... on IntEntityId {
            id
          }
        }
      }
    }
  }
`;

// GraphQL mutation to complete a stock take
const COMPLETE_STOCK_TAKE_MUTATION = `
  mutation CompleteStockTake($id: Int!) {
    stockTakes {
      stockTakeRecords {
        complete(id: $id) {
          __typename
          ... on Succeeded {
            succeeded
          }
        }
      }
    }
  }
`;

// Input type for creating/updating a stock take
interface StockTakeCreateInput {
  stockTakeReference?: string;
  description: string;
  stockTakeType: string;
  warehouseCode: string;
  warehouse: string;
  notes?: string;
  createdBy: string;
  assignedTo?: string;
  recurrence?: string;
  nextScheduledDate?: string;
  isSystemGenerated?: boolean;
  abcClass?: string;
}

// Response type for create/update mutation
interface MutationResponse {
  data?: {
    stockTakes?: {
      stockTakeRecords?: {
        create?: {
          __typename?: string;
          id?: number;
        };
        update?: {
          __typename?: string;
          id?: number;
        };
        complete?: {
          __typename?: string;
          succeeded?: boolean;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

export const useStockTakes = () => {
  const { fetchGraphQL } = useGraphQLFetch();
  const [stockTakes, setStockTakes] = useState<StockTake[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStockTakes = useCallback(
    async (
      options: {
        first?: number;
        after?: string;
        filter?: StockTakeFilterInput;
        sort?: StockTakeSortInput;
      } = {},
    ): Promise<{ stockTakes: StockTake[]; pageInfo: PageInfo | null }> => {
      setIsLoading(true);
      setError(null);

      const { first = 25, after, filter, sort } = options;

      const sortArray = sort ? [sort] : undefined;

      try {
        const result = await fetchGraphQL<GraphQLResponse["data"]>(STOCK_TAKES_QUERY, {
          first,
          after,
          filter,
          sort: sortArray,
        });

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const connection = result.data?.stockTakes?.stockTakeList;
        const itemsList = connection?.items?.map((item) => mapBackendToFrontend(item)) || [];
        const pageInfoResult = connection?.pageInfo || null;

        setStockTakes(itemsList);
        setPageInfo(pageInfoResult);

        return { stockTakes: itemsList, pageInfo: pageInfoResult };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to fetch stock takes";
        setError(message);
        console.error(err);
        toast.error(`Failed to load stock takes from backend: ${message}`);
        return { stockTakes: [], pageInfo: null };
      } finally {
        setIsLoading(false);
      }
    },
    [fetchGraphQL],
  );

    const createStockTake = useCallback(
      async (input: StockTakeCreateInput): Promise<number | null> => {
        setIsUpdating(true);
        setError(null);
        try {
          const result = await fetchGraphQL<MutationResponse["data"]>(CREATE_STOCK_TAKE_MUTATION, { stockTake: input });

          if (result.errors && result.errors.length > 0) {
            throw new Error(result.errors[0].message);
          }

          const createResult = result.data?.stockTakes?.stockTakeRecords?.create;

          if (createResult?.id) {
            toast.success("Stock take created successfully");
            return createResult.id;
          }

          return null;
        } catch (err) {
          const message = err instanceof Error ? err.message : "Failed to create stock take";
          setError(message);
          console.error(err);
          toast.error(`Failed to create stock take: ${message}`);
          return null;
        } finally {
          setIsUpdating(false);
        }
      },
      [fetchGraphQL],
    );

    const updateStockTake = useCallback(
      async (id: number, input: StockTakeCreateInput): Promise<boolean> => {
        setIsUpdating(true);
        setError(null);
        try {
          const result = await fetchGraphQL<MutationResponse["data"]>(UPDATE_STOCK_TAKE_MUTATION, { id, stockTake: input });

          if (result.errors && result.errors.length > 0) {
            throw new Error(result.errors[0].message);
          }

          const updateResult = result.data?.stockTakes?.stockTakeRecords?.update;

          if (updateResult?.id) {
            toast.success("Stock take updated successfully");
            return true;
          }

          return false;
        } catch (err) {
          const message = err instanceof Error ? err.message : "Failed to update stock take";
          setError(message);
          console.error(err);
          toast.error(`Failed to update stock take: ${message}`);
          return false;
        } finally {
          setIsUpdating(false);
        }
      },
      [fetchGraphQL],
    );

    const completeStockTake = useCallback(
      async (id: number): Promise<boolean> => {
        setIsUpdating(true);
        setError(null);
        try {
          const result = await fetchGraphQL<MutationResponse["data"]>(COMPLETE_STOCK_TAKE_MUTATION, { id });

          if (result.errors && result.errors.length > 0) {
            throw new Error(result.errors[0].message);
          }

          const completeResult = result.data?.stockTakes?.stockTakeRecords?.complete;

          if (completeResult?.succeeded) {
            toast.success("Stock take completed successfully");
            return true;
          }

          return false;
        } catch (err) {
          const message = err instanceof Error ? err.message : "Failed to complete stock take";
          setError(message);
          console.error(err);
          toast.error(`Failed to complete stock take: ${message}`);
          return false;
        } finally {
          setIsUpdating(false);
        }
      },
      [fetchGraphQL],
    );

  return {
    stockTakes,
    pageInfo,
    isLoading,
    isUpdating,
    error,
    fetchStockTakes,
    createStockTake,
    updateStockTake,
    completeStockTake,
  };
};
